import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Modal, Autocomplete, Avatar, Typography, Button } from '@ann/web-components';
import { NameInitialsAvatar } from 'react-name-initials-avatar';
import { useStore } from '../../../hooks';
import { useToast } from '../../../components/toast';
import { getAvatarUrl, generateAvatarColor } from '../../../utils/avatarUtils';
import { getModuleInitialRoute } from '../../../utils/routeUtils';
import websocketService from '../../../services/websocketService';
import {
  AutocompleteContainer,
  ModalBodyContent,
  WorkspaceListContainer,
  WorkspaceItem,
  WorkspaceItemLeft,
  WorkspaceItemRight,
  WorkspaceAvatar,
  WorkspaceAvatarWithInitials,
  CurrentWorkspaceText,
} from './SwitchWorkspaceModal.styled';

const SwitchWorkspaceModal = ({ isOpen, onClose, onWorkspaceSelect }) => {
  const { workspaceStore, authStore } = useStore();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [autocompleteValue, setAutocompleteValue] = useState('');
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [failedImages, setFailedImages] = useState(new Set());
  const autocompleteRef = useRef(null);
  
  const handleImageError = useCallback((workspaceId) => {
    setFailedImages((prev) => new Set([...prev, workspaceId]));
  }, []);
  useEffect(() => {
    console.log('workspaceStore.currentWorkspace', workspaceStore.currentWorkspace);
    console.log('authStore.currentWorkspaceUuid', authStore.currentWorkspaceUuid);
  }, [workspaceStore.currentWorkspace]);

  // Helper function to get workspace ID in a normalized format
  const getWorkspaceId = useCallback((workspace) => {
    if (!workspace) return null;
    
    // Try all possible ID fields (tenant_uuid is the primary field from API)
    const id = workspace.tenant_uuid ||
               workspace.tenantUuid ||
               workspace.tenant_id ||
               workspace.tenantId ||
               workspace.id ||
               workspace.workspaceUuid ||
               workspace.uuid ||
               null;
    
    return id ? String(id) : null;
  }, []);

  // Load data from store when modal opens
  useEffect(() => {
    if (isOpen) {
      // Only fetch if workspaces haven't been loaded yet
      if (!workspaceStore.workspaceList || workspaceStore.workspaceList.length === 0) {
        // Fetch with large page_size to get all available workspaces
        // Pasar authStore para sincronización con el workspace del token
        workspaceStore.fetchWorkspaceList({ page: 1, page_size: 100 }, authStore);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // NOTE: No need to sync currentWorkspace here - WorkspaceStore.fetchWorkspaceList already handles it
  // The store automatically sets the first workspace as current or validates the existing one

  // Generate suggestions based on workspace name (supports tenant_name | name)
  const suggestions = useMemo(() => {
    const workspaces = workspaceStore.workspaceList || [];
    
    return workspaces
      .map((workspace) => workspace.tenant_name || workspace.name || '')
      .filter(Boolean)
      .filter((workspaceName) => {
        if (!autocompleteValue.trim()) {
          return true;
        }
        
        const searchTerm = autocompleteValue.toLowerCase();
        return workspaceName.toLowerCase().includes(searchTerm);
      });
  }, [workspaceStore.workspaceList, autocompleteValue]);

  // Filter workspaces based on autocomplete value
  // Include currentWorkspace in dependencies to ensure re-render when it changes
  const filteredWorkspaces = useMemo(() => {
    const workspaces = workspaceStore.workspaceList || [];
    
    if (!autocompleteValue.trim()) {
      return workspaces;
    }
    
    const searchTerm = autocompleteValue.toLowerCase();
    return workspaces.filter((workspace) => {
      const workspaceName = workspace.tenant_name || workspace.name || '';
      return workspaceName.toLowerCase().includes(searchTerm);
    });
  }, [workspaceStore.workspaceList, workspaceStore.currentWorkspace, autocompleteValue]);

  const handleSwitchWorkspace = useCallback(async (workspace) => {
    // Update the current workspace in the store with the complete workspace object
    workspaceStore.setCurrentWorkspace(workspace, authStore);

    const tenantUuid = workspace.tenant_uuid || 
                       workspace.tenantUuid || 
                       workspace.workspaceUuid || 
                       workspace.workspace_uuid;
    
    if (tenantUuid && authStore.apiUserId) {
      try {
        await authStore.refreshTokenForWorkspace(tenantUuid);
        websocketService.reconnect();
      } catch (error) {
        console.error('Error refrescando token al cambiar workspace:', error);
        toast.error({
          title: 'Error',
          message: 'No se pudo actualizar el token. Por favor, recarga la página.',
          canClose: true,
        });
        return;
      }
    } else {
      console.warn('⚠️ No se pudo obtener tenantUuid o apiUserId:', { tenantUuid, apiUserId: authStore.apiUserId });
    }

    const workspaceName = workspace?.tenant_name || workspace?.name || workspace?.workspaceName || 'Workspace';
    toast.success({
      title: workspaceName,
      message: "You're now viewing data and settings for this workspace.",
      canClose: true,
    });

    const initialRoute = getModuleInitialRoute(location.pathname);
    if (initialRoute) {
      const normalizedCurrentPath = location.pathname.toLowerCase().replace(/\/$/, '');
      const normalizedInitialRoute = initialRoute.toLowerCase().replace(/\/$/, '');
      
      if (normalizedCurrentPath === normalizedInitialRoute) {
        window.location.href = initialRoute;
      } else {
        navigate(initialRoute, { replace: true });
      }
    }

    if (onWorkspaceSelect) {
      onWorkspaceSelect(workspace);
    }
  }, [onWorkspaceSelect, workspaceStore, authStore, toast, navigate, location.pathname]);

  const handleSearchChange = useCallback((value) => {
    setAutocompleteValue(value);
  }, []);

  const handleSuggestionSelect = useCallback(async (value) => {
    const workspaces = workspaceStore.workspaceList || [];
    const selectedWorkspace = workspaces.find(
      (workspace) => (workspace.tenant_name || workspace.name || workspace.workspaceName) === value
    );
    
    if (selectedWorkspace) {
      workspaceStore.setCurrentWorkspace(selectedWorkspace, authStore);

      const tenantUuid = selectedWorkspace.tenant_uuid || 
                         selectedWorkspace.tenantUuid || 
                         selectedWorkspace.workspaceUuid || 
                         selectedWorkspace.workspace_uuid;
      
      if (tenantUuid && authStore.apiUserId) {
        try {
          await authStore.refreshTokenForWorkspace(tenantUuid);
          websocketService.reconnect();
        } catch (error) {
          console.error('Error refrescando token al cambiar workspace:', error);
          toast.error({
            title: 'Error',
            message: 'No se pudo actualizar el token. Por favor, recarga la página.',
            canClose: true,
          });
          return;
        }
      } else {
        console.warn('⚠️ No se pudo obtener tenantUuid o apiUserId:', { tenantUuid, apiUserId: authStore.apiUserId });
      }

      const workspaceName = selectedWorkspace.tenant_name || selectedWorkspace.name || selectedWorkspace.workspaceName || 'Workspace';
      toast.success({
        title: workspaceName,
        message: "You're now viewing data and settings for this workspace.",
        canClose: true,
      });

      const initialRoute = getModuleInitialRoute(location.pathname);
      if (initialRoute) {
        const normalizedCurrentPath = location.pathname.toLowerCase().replace(/\/$/, '');
        const normalizedInitialRoute = initialRoute.toLowerCase().replace(/\/$/, '');
        
        if (normalizedCurrentPath === normalizedInitialRoute) {
          window.location.href = initialRoute;
        } else {
          navigate(initialRoute, { replace: true });
        }
      }

      if (onWorkspaceSelect) {
        onWorkspaceSelect(selectedWorkspace);
      }
    }
  }, [workspaceStore.workspaceList, workspaceStore, authStore, onWorkspaceSelect, toast, navigate, location.pathname]);

  const handleClear = useCallback(() => {
    setAutocompleteValue('');
    setAutocompleteKey((prev) => prev + 1);

    if (autocompleteRef.current) {
      const input = autocompleteRef.current.querySelector('input');
      if (input) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }, []);

  const handleClose = useCallback(() => {
    setAutocompleteValue('');
    setAutocompleteKey((prev) => prev + 1);
    onClose?.();
  }, [onClose]);

  // Helper function to check if a workspace is the current one
  const isCurrentWorkspace = useCallback((workspace, workspaceIndex) => {
    const currentWorkspace = workspaceStore.currentWorkspace;
    const fullList = workspaceStore.workspaceList || [];
    
    // If no currentWorkspace is set, the first workspace in the list is current
    if (!currentWorkspace && fullList.length > 0) {
      const firstWorkspace = fullList[0];
      const firstId = getWorkspaceId(firstWorkspace);
      const wsId = getWorkspaceId(workspace);
      const firstName = (firstWorkspace.tenant_name || firstWorkspace.name || '').trim().toLowerCase();
      const wsName = (workspace.tenant_name || workspace.name || '').trim().toLowerCase();
      
      // Compare by ID or name
      if (firstId && wsId && String(firstId).toLowerCase() === String(wsId).toLowerCase()) {
        return true;
      }
      if (firstName && wsName && firstName === wsName) {
        return true;
      }
      // If this is the first workspace in the full list, it's current
      return workspaceIndex === 0 && fullList[0] === workspace;
    }
    
    if (!currentWorkspace || !workspace) {
      return false;
    }
    
    // First, try to compare by ID (most reliable)
    const currentId = getWorkspaceId(currentWorkspace);
    const workspaceId = getWorkspaceId(workspace);
    
    if (currentId && workspaceId) {
      const normalizedCurrentId = String(currentId).trim().toLowerCase();
      const normalizedWorkspaceId = String(workspaceId).trim().toLowerCase();
      if (normalizedCurrentId === normalizedWorkspaceId) {
        return true;
      }
    }
    
    // Fallback: Compare by name (works when IDs are missing or don't match)
    // This is the most reliable fallback since names are always present
    const currentName = (currentWorkspace.tenant_name || currentWorkspace.name || '').trim().toLowerCase();
    const workspaceName = (workspace.tenant_name || workspace.name || '').trim().toLowerCase();
    return currentName && workspaceName && currentName === workspaceName;
  }, [workspaceStore.currentWorkspace, workspaceStore.workspaceList, getWorkspaceId]);

  // Early return if not open - after all hooks to comply with Rules of Hooks
  if (!isOpen) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      width="526px" 
      size="medium"
    >
      <Modal.Header>
        <Modal.Header.Title>Switch Workspace</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <AutocompleteContainer id="switch-workspace-autocomplete">
            <Autocomplete
              key={autocompleteKey}
              ref={autocompleteRef}
              value={autocompleteValue}
              onChange={handleSearchChange}
              onSearch={handleSearchChange}
              onSuggestionSelect={handleSuggestionSelect}
              onClear={handleClear}
              placeholder="Search workspace..."
              suggestions={suggestions}
              disabled={workspaceStore.isLoading}
            />
          </AutocompleteContainer>
          
          <WorkspaceListContainer>
            {filteredWorkspaces.map((workspace, filteredIndex) => {
              // Support multiple response formats (legacy + new backend)
              const workspaceId = getWorkspaceId(workspace);
              const workspaceName = workspace.tenant_name || workspace.name || '';
              
              // Skip if no valid ID or name
              if (!workspaceId && !workspaceName) {
                return null;
              }
              
              const photoUrl = workspace.photo_url || workspace.photoUrl;
              const avatarColor = generateAvatarColor(workspaceName);
              const imageFailed = workspaceId ? failedImages.has(workspaceId) : false;
              
              // Find the index of this workspace in the full list
              const fullList = workspaceStore.workspaceList || [];
              const fullListIndex = fullList.findIndex(ws => {
                const wsId = getWorkspaceId(ws);
                const wsName = ws.tenant_name || ws.name || '';
                return (wsId && wsId === workspaceId) || 
                       (wsName && wsName === workspaceName);
              });
              
              // Check if this workspace is the current one
              const isCurrent = isCurrentWorkspace(workspace, fullListIndex);
              
              // Get avatar URL (real or generated mock)
              const avatarUrl = imageFailed ? null : getAvatarUrl(photoUrl, workspaceName);
              const showInitialsAvatar = imageFailed || !avatarUrl;
              
              // Use a fallback key if workspaceId is null
              const itemKey = workspaceId || `workspace-${filteredIndex}-${workspaceName}`;
              
              return (
                <WorkspaceItem key={itemKey}>
                  <WorkspaceItemLeft>
                    {!showInitialsAvatar && avatarUrl ? (
                      <WorkspaceAvatar
                        src={avatarUrl}
                        size="lg"
                        onError={() => handleImageError(workspaceId)}
                      />
                    ) : (
                      <WorkspaceAvatarWithInitials>
                        <NameInitialsAvatar
                          name={workspaceName}
                          bgColor={avatarColor}
                          textColor="#ffffff"
                          size="21px"
                          borderRadius="50%"
                          textWeight="bold"
                          textSize="8px"
                          borderWidth="0"
                        />
                      </WorkspaceAvatarWithInitials>
                    )}
                    <Typography
                      category="subtitle"
                      weight="bold"
                      size="small"
                    >
                      {workspaceName}
                    </Typography>
                  </WorkspaceItemLeft>
                  <WorkspaceItemRight>
                    {isCurrent ? (
                      <CurrentWorkspaceText>
                        <Typography
                          category="body"
                          weight="regular"
                          size="small"
                        >
                          Current workspace
                        </Typography>
                      </CurrentWorkspaceText>
                    ) : (
                      <Button
                        iconRight="arrowRight"
                        label="Switch"
                        size="small"
                        type="tertiary"
                        onClick={() => handleSwitchWorkspace(workspace)}
                      />
                    )}
                  </WorkspaceItemRight>
                </WorkspaceItem>
              );
            })}
          </WorkspaceListContainer>
        </ModalBodyContent>
      </Modal.Body>
    </Modal>
  );
};

export default observer(SwitchWorkspaceModal);

